import type React from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Box, Fade, ClickAwayListener } from "~/common/mui";
import type { TreeNode, UsefulTool } from "~/domains/navigation/types/NavigationTypes";
import { UsefulToolsSection } from "~/features/categoryNavigation/components/UsefulToolsSection";
import { NavigationTree } from "~/features/categoryNavigation/components/shared";
import {
  DesktopNavigationMenu,
  DesktopNavigationMenuItem,
  NavigationDrawer,
} from "~/features/categoryNavigation/components/CategoryNavigation.styles";
import { useActiveCampaignsNotInSmartEdit } from "~/features/navigation/components/TopBannersSection/utils";
import { SALES_CAMPAIGN_PAGE } from "~/utils/url/staticUrls";

function pickUsefulTools(navigationTransitionNodes: TreeNode[]) {
  return (
    navigationTransitionNodes
      .slice()
      .reverse()
      .find((node) => node.usefulTools.length > 0)?.usefulTools ?? []
  );
}

interface DesktopNavigationProps {
  tree: TreeNode;
  nodesInTransition: TreeNode[];
  isOpen: boolean;
  offsetStyles: React.CSSProperties;
  selectNode: (node: TreeNode, noChildren?: boolean) => void;
  onMouseEnter: (node: TreeNode, noChildren?: boolean) => void;
  onMouseLeave: (node: TreeNode) => void;
  close: () => void;
  onExited: () => void;
}

export const DesktopNavigation = ({
  tree,
  nodesInTransition,
  isOpen,
  offsetStyles,
  selectNode,
  onMouseEnter,
  onMouseLeave,
  close,
  onExited,
}: DesktopNavigationProps) => {
  const navigationTransitionNodes = nodesInTransition.slice(1);
  const isSelected = (node: TreeNode) => nodesInTransition.includes(node);
  const usefulTools: UsefulTool[] = pickUsefulTools(navigationTransitionNodes);

  return (
    <ClickAwayListener onClickAway={close}>
      <div>
        <DesktopTopLevelNavigation
          style={offsetStyles}
          node={tree}
          onClick={selectNode}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          isSelected={isSelected}
        />
        <NavigationDrawer
          open={isOpen}
          anchor="top"
          onClose={close}
          style={offsetStyles}
          slots={{ transition: Fade }}
          slotProps={{ transition: { unmountOnExit: false, mountOnEnter: false, onExited } }}
          ModalProps={{
            keepMounted: true, // content stays mounted even when closed
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box sx={{ padding: 2 }}>
              <NavigationTree
                tree={tree}
                visibleNodes={navigationTransitionNodes}
                onItemClick={selectNode}
                onItemHover={onMouseEnter}
                onItemLeave={onMouseLeave}
              />
            </Box>
            {usefulTools.length > 0 && (
              <UsefulToolsSection
                usefulTools={usefulTools}
                navigationLevel={navigationTransitionNodes[navigationTransitionNodes.length - 1]}
              />
            )}
          </Box>
        </NavigationDrawer>
      </div>
    </ClickAwayListener>
  );
};

const DesktopTopLevelNavigation = ({
  node,
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected,
  style,
}: {
  node: TreeNode;
  onClick: (node: TreeNode) => void;
  onMouseEnter: (node: TreeNode) => void;
  onMouseLeave: (node: TreeNode) => void;
  isSelected: (node: TreeNode) => boolean;
  style?: React.CSSProperties;
}) => {
  const { t } = useTranslation();
  const campaignsNotInSmartEdit = useActiveCampaignsNotInSmartEdit();

  return (
    <DesktopNavigationMenu style={style}>
      {node.childNodes.map((item, index) => {
        const isSaleTab = item.gaLabel === "Sale %";
        if (isSaleTab && !campaignsNotInSmartEdit.length) {
          return null;
        }

        return (
          <DesktopNavigationMenuItem
            key={index}
            data-test-id={`header-navigation-l0-${index}`}
            onClick={
              isSaleTab
                ? () => {
                    window.location.href = SALES_CAMPAIGN_PAGE;
                  }
                : () => onClick(item)
            }
            onMouseEnter={isSaleTab ? undefined : () => onMouseEnter(item)}
            onMouseLeave={isSaleTab ? undefined : () => onMouseLeave(item)}
            className={classNames(isSelected(item) && "selected")}
          >
            <div className={classNames(isSaleTab && "saleTab")}>{t(item.title)}</div>
          </DesktopNavigationMenuItem>
        );
      })}
    </DesktopNavigationMenu>
  );
};
